import { isqrt } from "./dashboard";

/**
 * Sentinela de anomalias (T3.7, 06-ia-agentes §3): regras determinísticas sobre vendas, margens, descontos e estoque.
 * O modelo só explica o alerta; quem decide se é anomalia é este código.
 */

export const SENTINEL_DEFAULTS = {
  /** Ruptura: cobertura em dias (média de 28 dias) até a qual alerta. */
  stockoutDays: 7,
  /** Queda de canal: últimos 7 dias contra os 7 anteriores (pct4) e base mínima dos anteriores. */
  channelDropPct4: 300_000,
  channelMinBaseCents: 50_000,
  /** Desconto: σ acima da média do vendedor, distância mínima (pct4) e histórico mínimo (pedidos, 90 dias). */
  discountSigma: 3,
  discountMinGapPct4: 50_000,
  discountMinHistory: 10,
} as const;
export type SentinelConfig = {
  stockoutDays: number;
  channelDropPct4: number;
  channelMinBaseCents: number;
  discountSigma: number;
  discountMinGapPct4: number;
  discountMinHistory: number;
};

function rdiv(num: bigint, den: bigint): bigint {
  const neg = num < 0n;
  const a = neg ? -num : num;
  const q = (2n * a + den) / (2n * den);
  return neg ? -q : q;
}

export type NegativeMargin = {
  productId: string;
  name: string;
  revenueCents: number;
  costCents: number;
  lossCents: number;
};

/** Produtos vendidos com custo acima da receita (custo desconhecido fica de fora). */
export function negativeMargins(
  rows: readonly {
    productId: string;
    name: string;
    revenueCents: number;
    costCents: number;
    costMissing: boolean;
  }[],
): NegativeMargin[] {
  return rows
    .filter((r) => !r.costMissing && r.revenueCents > 0 && r.costCents > r.revenueCents)
    .map((r) => ({
      productId: r.productId,
      name: r.name,
      revenueCents: r.revenueCents,
      costCents: r.costCents,
      lossCents: r.costCents - r.revenueCents,
    }))
    .sort((a, b) => b.lossCents - a.lossCents);
}

/** Desconto total do pedido (itens + geral) em pct4 sobre o bruto dos itens. */
export function orderDiscountPct4(o: {
  itemsTotalCents: number;
  itemsDiscountCents: number;
  discountCents: number;
}): number {
  const gross = BigInt(o.itemsTotalCents) + BigInt(o.itemsDiscountCents);
  if (gross <= 0n) return 0;
  const disc = BigInt(Math.max(0, o.itemsDiscountCents)) + BigInt(Math.max(0, o.discountCents));
  return Number(rdiv(disc * 1_000_000n, gross));
}

export type DiscountOutlier = {
  orderId: string;
  number: number;
  sellerId: string;
  pct4: number;
  meanPct4: number;
  limitPct4: number;
  history: number;
};

/** Pedidos com desconto acima de média + kσ do próprio vendedor e pelo menos `minGap` acima da média. */
export function discountOutliers(
  history: ReadonlyMap<string, readonly number[]>,
  today: readonly { orderId: string; number: number; sellerId: string; pct4: number }[],
  cfg: Pick<
    SentinelConfig,
    "discountSigma" | "discountMinGapPct4" | "discountMinHistory"
  > = SENTINEL_DEFAULTS,
): DiscountOutlier[] {
  const stats = new Map<string, { mean: number; limit: number; n: number }>();
  for (const [seller, xs] of history) {
    const n = BigInt(xs.length);
    if (xs.length < cfg.discountMinHistory || n < 2n) continue;
    let s = 0n;
    let q = 0n;
    for (const x of xs) {
      s += BigInt(x);
      q += BigInt(x) * BigInt(x);
    }
    const mean = rdiv(s, n);
    const sigma = isqrt(rdiv(n * q - s * s, n * (n - 1n)));
    const limit = mean + BigInt(cfg.discountSigma) * sigma;
    const gap = mean + BigInt(cfg.discountMinGapPct4);
    stats.set(seller, { mean: Number(mean), limit: Number(limit > gap ? limit : gap), n: xs.length });
  }
  return today
    .flatMap((o) => {
      const st = stats.get(o.sellerId);
      if (!st || o.pct4 <= st.limit) return [];
      return [{ ...o, meanPct4: st.mean, limitPct4: st.limit, history: st.n }];
    })
    .sort((a, b) => b.pct4 - b.limitPct4 - (a.pct4 - a.limitPct4));
}

export type StockoutRisk = {
  productId: string;
  name: string;
  availableQ4: number;
  dailyQ4: number;
  coverageDays: number;
};

/** Produtos cuja posição (disponível + a receber) cobre até `days` dias pela média diária de 28 dias. */
export function stockoutRisks(
  rows: readonly {
    productId: string;
    name: string;
    availableQ4: number;
    onOrderQ4: number;
    sold28Q4: number;
  }[],
  days: number,
): StockoutRisk[] {
  return rows
    .flatMap((r) => {
      if (r.sold28Q4 <= 0) return [];
      const position = BigInt(r.availableQ4) + BigInt(Math.max(0, r.onOrderQ4));
      const cover = position <= 0n ? 0 : Number((position * 28n) / BigInt(r.sold28Q4));
      if (cover > days) return [];
      return [
        {
          productId: r.productId,
          name: r.name,
          availableQ4: r.availableQ4,
          dailyQ4: Number(rdiv(BigInt(r.sold28Q4), 28n)),
          coverageDays: cover,
        },
      ];
    })
    .sort((a, b) => a.coverageDays - b.coverageDays || a.name.localeCompare(b.name, "pt-BR"));
}

export type ChannelDrop = { channel: string; last7Cents: number; prev7Cents: number; dropPct4: number };

/** Canais com queda acima do limite (pct4) e base anterior mínima. */
export function channelDrops(
  rows: readonly { channel: string; last7Cents: number; prev7Cents: number }[],
  cfg: Pick<SentinelConfig, "channelDropPct4" | "channelMinBaseCents"> = SENTINEL_DEFAULTS,
): ChannelDrop[] {
  return rows
    .flatMap((r) => {
      if (r.prev7Cents < cfg.channelMinBaseCents || r.prev7Cents <= 0) return [];
      const drop = Number(
        rdiv((BigInt(r.prev7Cents) - BigInt(Math.max(0, r.last7Cents))) * 1_000_000n, BigInt(r.prev7Cents)),
      );
      return drop > cfg.channelDropPct4 ? [{ ...r, dropPct4: drop }] : [];
    })
    .sort((a, b) => b.dropPct4 - a.dropPct4);
}
